import { Stack } from '@mui/material';
import { OTPLoginContext } from 'Context/OTPLoginContext';
import { useContext, useState } from 'react';
import { useDictionary, useIsDesktop } from 'Hooks';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { HotAirBalloon } from 'Images';
import { ValidationSelector } from './ValidationSelector';
import { NextPrevButton } from 'Components/NextPrevButton';
import { OTPCodeValidationWrapper } from '../OTPCodeValidationWrapper';
import { generateOtpCode } from 'API/OTPLogin/OTPLogin';
import { IGenerateOTPCode } from 'API/OTPLogin/OTPLogin';
import { TitleImageAndText } from 'Components/TitleImageAndText';
import { ITextItem, TextBlock } from 'Components/TextBlock';
import { HTTP_STATUS_CODES } from 'Enum/HttpStatusCodes';
import { InvalidUserDialog } from './InvalidUserDialog';
import { CustomMetaTitle } from 'Components';
import { useLoader } from 'Hooks/useLoader';

export const PhoneEmailValidation = () => {
  const { step, setStep, userDetails } = useContext(OTPLoginContext);
  const [isEmail, setIsEmail] = useState<boolean>(false);
  const [validationModal, setValidationModal] = useState<boolean>(false);
  const { loader, isLoading, setIsLoading } = useLoader(
    undefined,
    { height: '100%' },
    50,
    false
  );
  const [showInvalidUserModal, setShowInvalidUserModal] =
    useState<boolean>(false);
  const isDesktop = useIsDesktop();

  const dictionary = useDictionary(
    Dictionary,
    DICTIONARY.OTP_Login
  ).phoneEmailValidation;

  const validationHandler = async () => {
    if (isEmail && userDetails.email.trim() === '') return;
    const requestBody: IGenerateOTPCode = {
      userIsrId: userDetails.IsrId,
      phone: userDetails.phonePrefix + userDetails.phoneNumber,
      isSms: !isEmail,
    };
    if (userDetails.email) requestBody.emailAddress = userDetails.email;
    setIsLoading(true);
    const result = await generateOtpCode(requestBody);
    if (result.status === HTTP_STATUS_CODES.OK) setValidationModal(true);
    if (result.request.status === HTTP_STATUS_CODES.BadRequest)
      setShowInvalidUserModal(true);
    setIsLoading(false);
  };

  const textBlockData: ITextItem[] = [
    { textData: dictionary.subTitle.lineOne },
    { textData: dictionary.subTitle.lineTwo },
  ];
  const mobileTextBlockData: ITextItem[] = [
    {
      textData: dictionary.subTitle.lineOne + ' ' + dictionary.subTitle.lineTwo,
    },
  ];

  return (
    <>
      <CustomMetaTitle
        disablePrefix
        title={
          Dictionary[DICTIONARY.OTP_Login].metaTitle +
          ' - ' +
          Dictionary[DICTIONARY.OTP_Login].metaTitleStep.stepThree
        }
      />
      <Stack>
        <TitleImageAndText
          title={dictionary.title}
          image={HotAirBalloon}
          showImage={isDesktop}
          testSx={{
            fontSize: '32px',
            lineHeight: '40px',
            width: isDesktop ? undefined : '250px',
          }}
        />
        <TextBlock
          texts={isDesktop ? textBlockData : mobileTextBlockData}
          sx={{ gap: '2px', mb: '56px' }}
        />
        <ValidationSelector isEmail={isEmail} setIsEmail={setIsEmail} />
        <Stack direction='row' justifyContent='center' gap='84px'>
          {isLoading ? (
            loader
          ) : (
            <>
              <NextPrevButton
                text={dictionary.backwards}
                callback={() => setStep(step - 1)}
              />
              <NextPrevButton
                text={dictionary.forward}
                isBlue
                isNext
                callback={validationHandler}
              />
            </>
          )}
        </Stack>
      </Stack>
      {validationModal && (
        <OTPCodeValidationWrapper
          isEmail={isEmail}
          setValidationModal={setValidationModal}
          sendCodeAgain={validationHandler}
        />
      )}
      <InvalidUserDialog
        isOpen={showInvalidUserModal}
        closeDialog={() => setShowInvalidUserModal(false)}
      />
    </>
  );
};
